import { Button, Card, Checkbox, Popover, Typography } from 'antd'
import { useMemo, useState } from 'react'
import { getMunicipality } from '../../../store/beslutsstod/municipalitySlice'
import { useSelector } from 'react-redux'
import ShowResults from '../showResults'
import { createTimeString } from '../utils/createTimeString'
import { Evaluation } from '../../../api/beslutsstod/beslutsstod'
import moment from 'moment'

interface InfoCardProps {
  evaluation: Evaluation
  archive: () => Promise<void>
}

export const InfoCard = ({ evaluation, archive }: InfoCardProps) => {
  // Note: if null text is returned, the field will not be displayed
  const texts = useMemo(() => {
    const tenant_id = localStorage.getItem('tenant_id')

    switch (tenant_id) {
      case process.env.REACT_APP_CLIENT_ID_HULTSFRED:
        return {
          totalExDouble: 'Ramtid',
          total: null,
        }

      default:
        return {
          totalExDouble: 'Total tid exklusive dubbelbemanning',
          total: 'Total tid inklusive dubbelbemanning',
        }
    }
  }, [])

  const municipalityData = useSelector(getMunicipality)
  const [expanded, setExpanded] = useState<boolean>(false)
  const [copyPopoverOpen, setCopyPopoverOpen] = useState(false)
  const [includeId, setIncludeId] = useState(false)
  const [includeComment, setIncludeComment] = useState(false)

  const handleExpandClick = () => {
    setExpanded(!expanded)
  }

  const inputState = JSON.parse(evaluation.input_state)

  if (!municipalityData?.input.calculationOutputSettings.unit) return null

  const totalExDouble = inputState.calculatedTime.totalExDouble ?? inputState.calculatedTime.totalHoursPerMonth - inputState.calculatedTime.hoursPerMonthTimePointsTasksDouble

  return (
    <div
      style={{
        marginLeft: 'auto',
        marginRight: 'auto',
        maxWidth: 'min(100%, 800px)',
        paddingBottom: '30px',
        textAlign: 'left',
        cursor: 'pointer',
      }}
    >
      <Card hoverable onClick={handleExpandClick}>
        <Card.Meta title={`${municipalityData?.input.formSettings.nameProcessId ?? 'ID'}: ${evaluation.process_id} ${evaluation.draft ? 'UTKAST' : ''}`} />
        <div style={{ padding: '10px' }}>
          {expanded ? (
            <>
              {evaluation.created && <Typography.Paragraph type="secondary">Skapad: {moment(evaluation.created).format('YYYY-MM-DD HH:mm')}</Typography.Paragraph>}
              <Typography>
                <ShowResults type="secondary" result={inputState.calculatedTime} unit={municipalityData.input.calculationOutputSettings.unit} />
              </Typography>
              {inputState.state?.checkboxes &&
                Object.keys(inputState.state.checkboxes).map((key, i) => (
                  <Typography.Paragraph type="secondary" key={i}>
                    {key}: {inputState.state.checkboxes[key] ? 'Ja' : 'Nej'}
                  </Typography.Paragraph>
                ))}
              {evaluation.note && (
                <Typography.Paragraph type="secondary">
                  <b>Anteckning:</b> {evaluation.note}
                </Typography.Paragraph>
              )}
            </>
          ) : (
            <>
              {evaluation.created && <Typography.Text type="secondary">Skapad: {moment(evaluation.created).format('YYYY-MM-DD HH:mm')}</Typography.Text>}
              {texts.totalExDouble && (
                <>
                  <br />
                  <Typography.Text type="secondary">
                    {texts.totalExDouble}: {createTimeString(totalExDouble, municipalityData.input.calculationOutputSettings.unit)}
                  </Typography.Text>
                </>
              )}
              {texts.total && (
                <>
                  <br />
                  <Typography.Text type="secondary">
                    {texts.total}: {createTimeString(inputState.calculatedTime.totalHoursPerMonth, municipalityData.input.calculationOutputSettings.unit)}
                  </Typography.Text>
                </>
              )}
              {evaluation.note && (
                <p>
                  <Typography.Text type="secondary">
                    <b>Anteckning:</b> {evaluation.note}
                  </Typography.Text>
                </p>
              )}
            </>
          )}
          <div style={{ marginTop: 10 }}>
            <Popover
              trigger="click"
              open={copyPopoverOpen}
              onOpenChange={(open) => {
                if (!open) {
                  setIncludeId(false)
                  setIncludeComment(false)
                }
                setCopyPopoverOpen(open)
              }}
              content={
                <div onClick={(e) => e.stopPropagation()}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <Checkbox checked disabled>
                      Personnummer
                    </Checkbox>

                    <Checkbox checked={includeId} onChange={(e) => setIncludeId(e.target.checked)} style={{ marginLeft: 0 }}>
                      {municipalityData?.input.formSettings.nameProcessId ?? 'ID'}
                    </Checkbox>

                    {evaluation.note && (
                      <Checkbox checked={includeComment} onChange={(e) => setIncludeComment(e.target.checked)} style={{ marginLeft: 0 }}>
                        Kommentar
                      </Checkbox>
                    )}
                  </div>
                  <Button type="primary" size="small" style={{ marginTop: 8 }} href={`/app/beslutsstod/${evaluation.id}?includeId=${includeId}&includeComment=${includeComment}`}>
                    Kopiera
                  </Button>
                </div>
              }
            >
              <Button
                type="primary"
                style={{ marginRight: 5 }}
                onClick={(e) => {
                  e.stopPropagation()
                }}
              >
                Kopiera tidigare underlag
              </Button>
            </Popover>
            <Button
              onClick={(e) => {
                e.preventDefault()
                e.stopPropagation()
                archive()
              }}
              type="primary"
            >
              Makulera
            </Button>
          </div>
        </div>
      </Card>
    </div>
  )
}
